/** One publication: the row on the Publish screen, the needs-attention card, the one-right-action buttons, and their sheets. */
import { useState } from "react";
import { Link, navigate } from "../../app/router";
import type { Publication } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, ConfirmDialog, Field, Modal, Progress, Segmented } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { useYoutubeSettings } from "../../data/queries";
import { dateTime, languageName } from "../../lib/format";
import { fromLocalInput, timeOnly, toLocalInput } from "../../lib/tz";
import { usePublishingSwitch } from "./PublishingPause";
import { AUDIT_FORM_URL, attentionInfo, canCancel, canEditMetadata, canReschedule, platformName, stateChip, uploadFraction } from "./pubState";

const INVALIDATE = ["publications", "publication", "dashboard", "queues"];

/** Opens a post's detail (sheet on phones, side panel on desktop) — from anywhere: /publish?…&focus=<id>. */
export function openPublication(id: string | null) {
  const onPublish = window.location.pathname === "/publish";
  const params = new URLSearchParams(onPublish ? window.location.search : "");
  if (id) params.set("focus", id);
  else params.delete("focus");
  const qs = params.toString();
  navigate(`/publish${qs ? `?${qs}` : ""}`, { replace: onPublish, keepScroll: onPublish });
}

export function usePubActions(p: Publication) {
  const api = useApi();
  const toast = useToast();
  const opts = (ok: string) => ({ invalidate: INVALIDATE, onSuccess: () => toast.ok(ok), onError: (e: unknown) => toast.error(e) });
  const rescheduled = (r: { warnings: string[] }) => {
    if (r.warnings.length) toast.show({ tone: "info", sticky: true, text: `Rescheduled. ${r.warnings.join(" ")}` });
    else toast.ok("Rescheduled.");
  };
  return {
    retry: useMutation(() => api.publicationAction(p.id, "retry"), opts("Retrying.")),
    cancel: useMutation(() => api.publicationAction(p.id, "cancel"), opts("Cancelled.")),
    reschedule: useMutation((iso: string) => api.reschedulePublication(p.id, iso), { invalidate: INVALIDATE, onSuccess: rescheduled, onError: (e: unknown) => toast.error(e) }),
    publishNow: useMutation(() => api.reschedulePublication(p.id, new Date().toISOString()), opts("Publishing now.")),
  };
}

type Sheet = "reschedule" | "cancel" | "edit" | null;

/** The sheets every publication surface shares. */
export function PubSheets({ p, sheet, setSheet, a }: { p: Publication; sheet: Sheet; setSheet: (s: Sheet) => void; a: ReturnType<typeof usePubActions> }) {
  if (sheet === "cancel") {
    return (
      <ConfirmDialog title="Cancel this post?" confirmLabel="Cancel post" busy={a.cancel.pending}
        impact={`“${p.episode.title}” will not be posted to ${platformName(p.platform)} · ${p.account_name}.${p.platform_scheduled ? " It is already uploaded and scheduled there; it will stay private." : ""} Other posts are unaffected.`}
        onCancel={() => setSheet(null)} onConfirm={() => void a.cancel.mutate().then(() => setSheet(null))} />
    );
  }
  if (sheet === "reschedule") return <RescheduleSheet initial={p.scheduled_at} busy={a.reschedule.pending} onClose={() => setSheet(null)} onSave={(iso) => void a.reschedule.mutateAsync(iso).then(() => setSheet(null), () => undefined)} />;
  if (sheet === "edit") return <MetadataSheet p={p} onClose={() => setSheet(null)} />;
  return null;
}

function Headline({ p }: { p: Publication }) {
  return (
    <>
      <p className="pub-title">
        <button type="button" className="pub-open" onClick={() => openPublication(p.id)} aria-label={`Details: ${p.episode.title}`}>
          {p.episode.sequence ? <span className="muted">Ep {p.episode.sequence} · </span> : null}{p.metadata.title || p.episode.title}
        </button>
      </p>
      <p className="pub-meta muted small">{p.series.title} · {languageName(p.language_code)} · <b>{platformName(p.platform)}</b> {p.account_name}</p>
    </>
  );
}

/** "Waiting for Ep 3 — failed · open": the post this one is waiting behind (series order). */
export function BlockerLink({ p }: { p: Publication }) {
  const b = p.hold_detail?.blocker;
  if (!b) return null;
  return (
    <button type="button" className="text-link blocker-link" onClick={() => openPublication(b.publication_id)}>
      Waiting for Ep {b.sequence} — {b.state.replace(/_/g, " ")} · open
    </button>
  );
}

/** A row in Upcoming / History. */
export function PublicationRow({ p, showDate }: { p: Publication; showDate?: boolean }) {
  const a = usePubActions(p);
  const [sheet, setSheet] = useState<Sheet>(null);
  const chip = stateChip(p);
  const f = p.state === "publishing" ? uploadFraction(p) : null;
  const when = p.published_at ?? p.scheduled_at;
  return (
    <article className={`pub tone-${chip.tone}`} aria-label={`${p.episode.title} · ${p.account_name}`}>
      <div className="pub-top">
        <Badge tone={chip.tone}>{chip.label}</Badge>
        <time className="muted small push" dateTime={when ?? undefined}>{when ? (showDate ? dateTime(when) : timeOnly(when)) : "As soon as possible"}</time>
      </div>
      <Headline p={p} />
      {f != null && <Progress value={f} label={`Upload progress for ${p.episode.title}`} tone="info" />}
      {p.hold_detail?.waiting && <p className="pub-reason small muted">{p.hold_detail.message}</p>}
      {p.state === "retry_pending" && p.last_error?.message && <p className="pub-error"><Icon name="alert" size={18} /> {p.last_error.message}{p.next_attempt_at ? ` · next try ${timeOnly(p.next_attempt_at)}` : ""}</p>}
      <div className="pub-actions">
        {p.platform_url && <a className="btn btn-ghost btn-sm" href={p.platform_url} target="_blank" rel="noreferrer"><Icon name="link" size={18} /><span>Open on {platformName(p.platform)}</span></a>}
        {canReschedule(p) && <Button size="sm" variant="ghost" icon="calendar" onClick={() => setSheet("reschedule")}>Reschedule</Button>}
        {canEditMetadata(p) && <Button size="sm" variant="ghost" icon="edit" onClick={() => setSheet("edit")}>Edit</Button>}
        {canCancel(p) && <Button size="sm" variant="ghost" onClick={() => setSheet("cancel")}>Cancel</Button>}
      </div>
      <PubSheets p={p} sheet={sheet} setSheet={setSheet} a={a} />
    </article>
  );
}

/** The one right action for a post that needs the owner (per hold_reason; see pubState.HOLD_REASONS). */
export function AttentionActions({ p, a, setSheet }: { p: Publication; a: ReturnType<typeof usePubActions>; setSheet: (s: Sheet) => void }) {
  const api = useApi();
  const pause = usePublishingSwitch();
  const yt = useYoutubeSettings();
  const info = attentionInfo(p);
  switch (info.action) {
    case "retry":
      return <Button size="sm" variant="primary" icon="refresh" busy={a.retry.pending} onClick={() => void a.retry.mutate()}>Retry</Button>;
    case "check_again":
      return (
        <>
          <Button size="sm" variant="primary" icon="refresh" busy={a.retry.pending} onClick={() => void a.retry.mutate()}>Check again</Button>
          {p.platform_url && <a className="btn btn-ghost btn-sm" href={p.platform_url} target="_blank" rel="noreferrer"><Icon name="external" size={18} /><span>Open on {platformName(p.platform)}</span></a>}
        </>
      );
    case "reconnect":
      return <Button size="sm" variant="primary" icon="lock" onClick={() => api.connectAccount(p.account_id)}>Reconnect account</Button>;
    case "reschedule":
      return (
        <>
          <Button size="sm" variant="primary" icon="calendar" onClick={() => setSheet("reschedule")}>Reschedule</Button>
          <Button size="sm" variant="secondary" icon="publish" busy={a.publishNow.pending} onClick={() => void a.publishNow.mutate()}>Publish now</Button>
        </>
      );
    case "audit":
      // Once the audit has passed, a post still held for it is released with a retry (the API re-checks the gate).
      if (yt.data?.api_audited) return <Button size="sm" variant="primary" icon="play" busy={a.retry.pending} onClick={() => void a.retry.mutate()}>Release</Button>;
      return (
        <>
          <Link to="/settings/accounts#youtube" className="btn btn-primary btn-sm"><Icon name="settings" size={18} /><span>Audit status</span></Link>
          <a className="btn btn-ghost btn-sm" href={AUDIT_FORM_URL} target="_blank" rel="noreferrer"><Icon name="link" size={18} /><span>Audit form</span></a>
        </>
      );
    case "resume":
      return <>{<Button size="sm" variant="primary" icon="play" busy={pause.put.pending} onClick={() => void pause.put.mutate({ paused: false })}>Resume publishing</Button>}{pause.confirm}</>;
    case "queues":
      return <Link to="/settings/queues" className="btn btn-primary btn-sm"><Icon name="calendar" size={18} /><span>Open queues</span></Link>;
    case "accounts":
      return <Link to="/settings/accounts" className="btn btn-primary btn-sm"><Icon name="user" size={18} /><span>Open accounts</span></Link>;
    case "review":
      return <Link to={`/studio/${p.series.id}`} className="btn btn-primary btn-sm"><Icon name="studio" size={18} /><span>Open the series</span></Link>;
    case "blocker":
      return p.hold_detail?.blocker ? <Button size="sm" variant="primary" onClick={() => openPublication(p.hold_detail!.blocker!.publication_id)}>Open Ep {p.hold_detail.blocker.sequence}</Button> : null;
    case "cancel":
      return <Button size="sm" variant="primary" onClick={() => setSheet("cancel")}>Cancel this post</Button>;
    case "youtube":
      return p.platform_url ? <a className="btn btn-primary btn-sm" href={p.platform_url} target="_blank" rel="noreferrer"><Icon name="external" size={18} /><span>Open on {platformName(p.platform)}</span></a> : null;
    default:
      return null;
  }
}

/** A card in "Needs attention": what went wrong in plain words (the server's sentence), and the one right action. */
export function AttentionCard({ p }: { p: Publication }) {
  const a = usePubActions(p);
  const [sheet, setSheet] = useState<Sheet>(null);
  const info = attentionInfo(p);
  const chip = stateChip(p);
  return (
    <article className={`pub tone-${chip.tone === "attention" ? "attention" : "danger"}`} aria-label={`${p.episode.title} · ${p.account_name}`}>
      <div className="pub-top">
        <Badge tone={chip.tone}>{chip.label}</Badge>
        <time className="muted small push" dateTime={p.scheduled_at ?? undefined}>{p.scheduled_at ? dateTime(p.scheduled_at) : ""}</time>
      </div>
      <Headline p={p} />
      <p className="pub-reason">{info.reason}</p>
      <BlockerLink p={p} />
      <div className="pub-actions">
        <AttentionActions p={p} a={a} setSheet={setSheet} />
        {canEditMetadata(p) && <Button size="sm" variant="ghost" icon="edit" onClick={() => setSheet("edit")}>Edit</Button>}
        {info.action !== "cancel" && canCancel(p) && <Button size="sm" variant="ghost" onClick={() => setSheet("cancel")}>Cancel</Button>}
      </div>
      <PubSheets p={p} sheet={sheet} setSheet={setSheet} a={a} />
    </article>
  );
}

export function RescheduleSheet({ initial, onSave, onClose, busy }: { initial: string | null | undefined; onSave: (iso: string) => void; onClose: () => void; busy: boolean }) {
  const [value, setValue] = useState(toLocalInput(initial && Date.parse(initial) > Date.now() ? initial : null));
  const iso = fromLocalInput(value);
  const valid = iso !== null && Date.parse(iso) > Date.now();
  return (
    <Modal title="Reschedule" variant="sheet" onClose={onClose} footer={<><Button variant="ghost" onClick={onClose}>Close</Button><Button type="submit" form="resched-form" variant="primary" busy={busy} disabled={!valid}>Save time</Button></>}>
      <form id="resched-form" className="stack" onSubmit={(e) => { e.preventDefault(); if (valid && iso) onSave(iso); }}>
        <label className="field-label" htmlFor="resched">New time (your time zone)</label>
        <input id="resched" type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
        {!valid && <p className="field-error">Pick a time in the future.</p>}
        <p className="field-hint">The series order still applies. If it's already uploaded, YouTube's scheduled time moves too.</p>
      </form>
    </Modal>
  );
}

const TITLE_MAX = 100;
const DESC_MAX_BYTES = 5000;
const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;

export function MetadataSheet({ p, onClose }: { p: Publication; onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const [title, setTitle] = useState(p.metadata.title);
  const [description, setDescription] = useState(p.metadata.description);
  const [tags, setTags] = useState(p.metadata.tags.join(", "));
  const [privacy, setPrivacy] = useState(p.metadata.privacy);
  const save = useMutation(() => api.updatePublicationMetadata(p.id, {
    title: title.trim(), description, tags: tags.split(",").map((t) => t.trim()).filter(Boolean), privacy,
  }), { invalidate: ["publications", "publication"], onSuccess: () => { toast.ok("Details saved."); onClose(); }, onError: (e) => toast.error(e, "Couldn't save") });
  const angle = /[<>]/;
  const titleErr = !title.trim() ? "A title is required." : title.length > TITLE_MAX ? `At most ${TITLE_MAX} characters (${title.length}).` : angle.test(title) ? "YouTube doesn't allow < or >." : undefined;
  const bytes = utf8Bytes(description);
  const descErr = bytes > DESC_MAX_BYTES ? `Too long for YouTube: ${bytes} of ${DESC_MAX_BYTES} bytes.` : angle.test(description) ? "YouTube doesn't allow < or >." : undefined;
  return (
    <Modal title="Edit post details" variant="sheet" onClose={onClose} footer={<><Button variant="ghost" onClick={onClose}>Close</Button><Button variant="primary" busy={save.pending} disabled={!!titleErr || !!descErr} onClick={() => void save.mutate()}>Save details</Button></>}>
      <Field label="Title" error={titleErr} hint={`${title.length}/${TITLE_MAX}`}>{(id, d) => <input id={id} aria-describedby={d} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
      <Field label="Description" error={descErr}>{(id, d) => <textarea id={id} aria-describedby={d} rows={5} value={description} onChange={(e) => setDescription(e.target.value)} dir="auto" />}</Field>
      <Field label="Tags" optional hint="Separated by commas">{(id, d) => <input id={id} aria-describedby={d} value={tags} onChange={(e) => setTags(e.target.value)} />}</Field>
      <Segmented label="Visibility" value={privacy} onChange={setPrivacy} options={[{ value: "public", label: "Public" }, { value: "unlisted", label: "Unlisted" }, { value: "private", label: "Private" }]} />
    </Modal>
  );
}
